import { useState, useEffect } from "react";
import { ArrowDown, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import sampleImg from "@/assets/compression-sample.jpg";

const formatSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / 1024).toFixed(0) + " KB";
};

const CompressionExample = () => {
  const [originalSize, setOriginalSize] = useState<number | null>(null);
  const [compressedSize, setCompressedSize] = useState<number | null>(null);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);

  useEffect(() => {
    const loadAndCompress = async () => {
      try {
        const response = await fetch(sampleImg);
        const blob = await response.blob();
        setOriginalSize(blob.size);

        const img = new Image();
        img.src = sampleImg;
        await new Promise((resolve) => { img.onload = resolve; });

        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (b) => {
            if (b) {
              setCompressedSize(b.size);
              setCompressedUrl(URL.createObjectURL(b));
            }
          },
          "image/jpeg",
          0.8
        );
      } catch (e) {
        console.error("Error calculating compression:", e);
      }
    };
    loadAndCompress();

    return () => {
      if (compressedUrl) URL.revokeObjectURL(compressedUrl);
    };
  }, []);

  const reduction = originalSize && compressedSize
    ? Math.round(((originalSize - compressedSize) / originalSize) * 100)
    : null;

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-foreground text-center mb-6">
        Así se reduce el tamaño de tus imágenes
      </h2>

      <div className="bg-card rounded-xl shadow-md p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
          {/* Original */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-full aspect-[4/3] rounded-lg bg-muted overflow-hidden">
              <img src={sampleImg} alt="Imagen original sin comprimir" className="w-full h-full object-cover" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">Original</p>
              <p className="text-muted-foreground text-sm">
                {originalSize ? formatSize(originalSize) : "Calculando..."}
              </p>
            </div>
          </div>

          {/* Arrow */}
          <div className="flex items-center justify-center py-2 md:py-0">
            <ArrowDown className="w-6 h-6 text-muted-foreground md:hidden" />
            <ArrowRight className="w-6 h-6 text-muted-foreground hidden md:block" />
          </div>

          {/* Optimized - uses the real compressed blob */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-full aspect-[4/3] rounded-lg bg-muted overflow-hidden ring-2 ring-green-500/30">
              <img
                src={compressedUrl || sampleImg}
                alt="Imagen optimizada comprimida"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">Optimizada</p>
              <p className="text-muted-foreground text-sm">
                {compressedSize ? formatSize(compressedSize) : "Calculando..."}
              </p>
            </div>
          </div>
        </div>

        {/* Reduction badge */}
        <div className="flex justify-center mt-6">
          <Badge className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-1.5 rounded-full">
            {reduction !== null ? `Reducción: ${reduction}%` : "Calculando..."}
          </Badge>
        </div>

        {/* Result summary */}
        {originalSize && compressedSize && reduction !== null && (
          <div className="flex flex-wrap items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
            <span>Original: <span className="font-medium text-foreground">{formatSize(originalSize)}</span></span>
            <span>Optimizada: <span className="font-medium text-foreground">{formatSize(compressedSize)}</span></span>
            <span>Ahorraste: <span className="text-green-600 font-bold">{formatSize(originalSize - compressedSize)} ({reduction}%)</span></span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompressionExample;
